import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { 
  FaUser, 
  FaEnvelope, 
  FaLock, 
  FaCheck, 
  FaTrophy, 
  FaWhistle, 
  FaClipboardList, 
  FaBullhorn,
  FaShieldAlt,
  FaGlobe,
  FaCheckCircle,
  FaEnvelopeOpenText,
  FaPhoneAlt,
  FaFacebookF,
  FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaToggleOn,
  FaToggleOff
} from 'react-icons/fa';
import '../styles/pages/Signup.scss';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SportsIcon from '@mui/icons-material/Sports';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SchoolIcon from '@mui/icons-material/School';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';

// Import other necessary components
import InputField from '../components/InputField';
import phone from '../images/phone.png';
import { useJoinModal } from '../components/JoinModalProvider';

const Signup = () => {
  const { openModal } = useJoinModal();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    teamName: '',
    teamType: '',
    isOnFirstWhistle: '',
    managerName: '',
    managerMobile: ''
  });
  
  const [contactData, setContactData] = useState({
    name: '',
    message: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [contactErrors, setContactErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [contactSubmitStatus, setContactSubmitStatus] = useState(null);
  
  const signupFormRef = useRef(null);
  
  const [isAnnual, setIsAnnual] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear the error for this field when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
    
    // Reset submit status when form is changed
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };
  
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData({
      ...contactData,
      [name]: value
    });
    
    // Clear the error for this field when user types
    if (contactErrors[name]) {
      setContactErrors({
        ...contactErrors,
        [name]: ''
      });
    }
    
    if (contactSubmitStatus) {
      setContactSubmitStatus(null);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.teamType) {
      errors.teamType = 'Please select an option';
    }
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9\s+()-]{10,15}$/.test(formData.mobile.trim())) {
      errors.mobile = 'Please enter a valid mobile number';
    }
    
    if (formData.teamType === 'Parent' || formData.teamType === 'Player') {
      if (!formData.isOnFirstWhistle) {
        errors.isOnFirstWhistle = 'Please select an option';
      }
      
      if (!formData.teamName.trim()) {
        errors.teamName = 'Team name is required';
      }
      
      if (formData.isOnFirstWhistle === 'false') {
        if (!formData.managerName.trim()) {
          errors.managerName = 'Manager name is required';
        }
        
        if (!formData.managerMobile.trim()) {
          errors.managerMobile = 'Manager mobile number is required';
        } else if (!/^[0-9\s+()-]{10,15}$/.test(formData.managerMobile.trim())) {
          errors.managerMobile = 'Please enter a valid mobile number';
        }
      }
    } else {
      if (!formData.teamName.trim()) {
        errors.teamName = 'Team name is required';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateContactForm = () => {
    const errors = {};
    
    if (!contactData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!contactData.message.trim()) {
      errors.message = 'Message is required';
    }
    
    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setFormErrors({});
    
    try {
      console.log('Form data being submitted:', formData);
      
      const requestData = {
        ...formData,
        captchaToken: 'direct-signup-page'
      };
      
      console.log('Request data:', requestData);
      
      const response = await fetch('/api/join-waitlist.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData),
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      let data;
      let errorMessage;
      
      try {
        const textResponse = await response.text();
        console.log('Raw response:', textResponse);
        
        try {
          data = JSON.parse(textResponse);
          console.log('Parsed response data:', data);
        } catch (parseError) {
          console.error('Failed to parse response as JSON:', parseError);
          errorMessage = 'Server returned invalid response format';
          throw new Error(errorMessage);
        }
      } catch (textError) {
        console.error('Failed to read response text:', textError);
        errorMessage = 'Failed to read server response';
        throw new Error(errorMessage);
      }
      
      if (!response.ok) {
        errorMessage = data?.error || `Server error: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      // Success handling
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        mobile: '',
        teamName: '',
        teamType: '',
        isOnFirstWhistle: '',
        managerName: '',
        managerMobile: ''
      });
      setFormErrors({});
      
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setFormErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to submit form. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
      // Scroll to the top on submission
      window.scrollTo(0, 0);
    }
  };
  
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateContactForm()) {
      return;
    }
    
    setIsContactSubmitting(true);
    
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await window.grecaptcha.execute('6LdleMEqAAAAACjtGkwMXYO2fQXJoc-VpRQXSjpu', { action: 'contact_form_submit' });
      
      const response = await fetch('/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          name: contactData.name,
          message: contactData.message,
          recaptchaToken: recaptchaToken
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Error sending message');
      }
      
      // Success handling
      setContactSubmitStatus('success');
      
      // Reset form after successful submission
      setContactData({
        name: '',
        message: ''
      });
      setContactErrors({});
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setContactSubmitStatus('error');
      setContactErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to send message. Please try again.'
      }));
    } finally {
      setIsContactSubmitting(false);
    }
  };
  
  // Helper function to check if we need to show the FirstWhistle check
  const shouldShowFirstWhistleCheck = () => {
    return formData.teamType === 'Parent' || formData.teamType === 'Player';
  };
  
  const executeScroll = () => {
    const userTypeSection = document.querySelector('.user-type-selection');
    if (userTypeSection) {
      userTypeSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Bokeh effect circles for the header
  const renderBokehCircles = () => {
    return (
      <svg className="bokeh" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10%" cy="85%" r="75%"  />
        <circle cx="45%" cy="50%" r="15%"   />
        <circle cx="85%" cy="35%" r="30%"  />
        <circle cx="60%" cy="85%" r="20%"   />
        <circle cx="45%" cy="50%" r="30%"   />
        <circle cx="35%" cy="25%" r="20%"   />
        <circle cx="90%" cy="-25%" r="35%"  />
        <circle cx="-15%" cy="30%" r="30%"  />
        <circle cx="65%" cy="85%" r="55%"   />
        <circle cx="45%" cy="50%" r="20%"   />
      </svg>
    );
  };

  return (
    <div className="signup-page">
      <Helmet>
        <title>Join FirstWhistle | AI-Powered Football Team Management App</title>
        <meta name="description" content="Sign up for FirstWhistle - the AI-powered football management app. Free for managers and coaches, with premium features for players and parents." />
        <meta property="og:title" content="Join FirstWhistle | AI-Powered Football Team Management App" />
        <meta property="og:description" content="Sign up for FirstWhistle - the AI-powered football management app. Free for managers and coaches, with premium features for players and parents." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://firstwhistle.app/signup" />
        <link rel="canonical" href="https://firstwhistle.app/signup" />
      </Helmet>
      
      <div className="header-container">
        {renderBokehCircles()}
        
        <div className="signup-hero-section">
          <div className="signup-hero-content">
            <h1>Join FirstWhistle Today</h1>
            <h3>Introducing the <span>First AI-Powered</span> Football Team Manager</h3>
            
            <div className="video-showcase mobile-video">
              <video 
                className="promo-video" 
                controls 
                loop 
                playsInline
                preload="auto"
              >
                <source src="/videos/firstwhistle-promo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            
            <div className="waitlist-actions">
              <button 
                className="hero-join-button"
                onClick={openModal}
              >
                Join FirstWhistle - Its Free!
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container">
        {submitStatus === 'success' ? (
          <div className="success-container">
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Thank you for joining FirstWhistle!</h2>
              {shouldShowFirstWhistleCheck() && formData.isOnFirstWhistle === 'true' ? (
                <p>Great to have you on board! We will confirm with the club and approve access - we will be in touch soon.</p>
              ) : (
                <p>We've received your information and will be in touch soon.</p>
              )}
              <button className="primary-button" onClick={() => setSubmitStatus(null)}>Register Another User</button>
            </div>
          </div>
        ) : (
          <div className="signup-container">
            <div className="free-highlight">
              <div className="highlight-content">
                <h2>Free for Coaches & Managers</h2>
                <p>FirstWhistle is 100% free for coaches and team managers - premium features only apply to Players and Parents</p>
              </div>
            </div>
            
            <div className="pricing-info">
              <h2>FirstWhistle Pricing</h2>
              <p className="intro-text">To sign up is easy and free! We only charge a small fee for premium features.</p>
              
              <div className="pricing-toggle">
                <span className={!isAnnual ? "active" : ""}>Monthly</span>
                <button className="toggle-button" onClick={() => setIsAnnual(!isAnnual)}>
                  {isAnnual ? <FaToggleOn /> : <FaToggleOff />}
                </button>
                <span className={isAnnual ? "active" : ""}>Annual</span>
                {isAnnual && <div className="annual-savings"><span className="savings-text">Save </span><span className="percentage">30%</span></div>}
              </div>
              
              <div className="pricing-grid">
                <div className="pricing-card free">
                  <h3>Free Plan</h3>
                  <div className="price">£0</div>
                  <p className="price-subtitle">For Managers & Coaches</p>
                  <div className="features">
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Full team management
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Match scheduling
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Player tracking
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Basic statistics
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Team communication
                    </div>
                  </div>
                  <button onClick={openModal} className="pricing-cta-button">Join Now</button>
                </div>
                
                <div className="pricing-card premium">
                  <h3>Premium Features</h3>
                  <div className="price">
                    {isAnnual ? (
                      <>
                        <span className="original-price">£30</span>
                        <span className="current-price">£25</span>
                      </>
                    ) : '£2.50'}
                  </div>
                  <p className="price-subtitle">{isAnnual ? 'Per year' : 'Per month'} for Players & Parents</p>
                  {isAnnual && (
                    <div className="annual-saving">
                      Save 30% on annual plan
                    </div>
                  )}
                  <div className="features">
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Full leaderboard access
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> All achievements
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Match archive
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Advanced statistics
                    </div>
                    <div className="feature-item">
                      <CheckCircleOutlineIcon /> Performance insights
                    </div>
                  </div>
                  <button onClick={openModal} className="pricing-cta-button">Join Now</button>
                </div>
              </div>
              
              <p className="pricing-note">*All basic features are free for everyone. Premium features require a small fee for Players and Parents only.</p>
            </div>
            
            <div className="user-type-selection">
              <h2>Who are you signing up as?</h2>
              <p>Select your role to get started with FirstWhistle</p>
              
              <div className="user-type-grid">
                <div 
                  className={`user-type-card ${formData.teamType === 'Player' ? 'active' : ''} player-card`}
                  onClick={() => setFormData({...formData, teamType: 'Player'})}
                >
                  <SportsSoccerIcon />
                  <h3>Player</h3>
                  <p>Track your performance, view achievements, and never miss a match</p>
                </div>
                
                <div 
                  className={`user-type-card ${formData.teamType === 'Parent' ? 'active' : ''} parent-card`}
                  onClick={() => setFormData({...formData, teamType: 'Parent'})}
                >
                  <FamilyRestroomIcon />
                  <h3>Parent</h3>
                  <p>Stay connected with your child's football activities and progress</p>
                </div>
                
                <div 
                  className={`user-type-card ${formData.teamType === 'Player / Organiser' ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, teamType: 'Player / Organiser'})}
                >
                  <div className="free-badge">FREE</div>
                  <SportsIcon />
                  <h3>Player / Organiser</h3>
                  <p>Manage your weekly game while tracking your own performance</p>
                </div>
                
                <div 
                  className={`user-type-card ${formData.teamType === 'Coach (Academy)' ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, teamType: 'Coach (Academy)'})}
                >
                  <div className="free-badge">FREE</div>
                  <SchoolIcon />
                  <h3>Coach (Academy)</h3>
                  <p>Develop your players with advanced tracking and feedback tools</p>
                </div>
                
                <div 
                  className={`user-type-card ${formData.teamType === 'Manager - Sat / Sun league' ? 'active' : ''}`}
                  onClick={() => setFormData({...formData, teamType: 'Manager - Sat / Sun league'})}
                >
                  <div className="free-badge">FREE</div>
                  <SupervisorAccountIcon />
                  <h3>Manager</h3>
                  <p>Sat/Sun league management made simple with our comprehensive tools</p>
                </div>
              </div>
            </div>
            
            {formData.teamType && (
              <div id="signup-form-container" className="signup-form-container" ref={signupFormRef}>
                <h2>Complete Your Registration</h2>
                <p>Please fill in your details to join FirstWhistle</p>
                
                <form className="signup-form" onSubmit={handleSubmit}>
                  {submitStatus === 'error' && formErrors.submit && (
                    <div className="error-message global-error">
                      {formErrors.submit}
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="Your first name"
                      className={formErrors.firstName ? 'error' : ''}
                    />
                    {formErrors.firstName && <span className="error-message">{formErrors.firstName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Your last name"
                      className={formErrors.lastName ? 'error' : ''}
                    />
                    {formErrors.lastName && <span className="error-message">{formErrors.lastName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="mobile">Mobile</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="Your mobile number"
                      className={formErrors.mobile ? 'error' : ''}
                    />
                    {formErrors.mobile && <span className="error-message">{formErrors.mobile}</span>}
                  </div>
                  
                  {shouldShowFirstWhistleCheck() ? (
                    <>
                      <div className="form-group">
                        <label htmlFor="isOnFirstWhistle">My Club is already on FirstWhistle</label>
                        <select
                          id="isOnFirstWhistle"
                          name="isOnFirstWhistle"
                          value={formData.isOnFirstWhistle}
                          onChange={handleChange}
                          className={formErrors.isOnFirstWhistle ? 'error' : ''}
                        >
                          <option value="">Select option</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                        {formErrors.isOnFirstWhistle && <span className="error-message">{formErrors.isOnFirstWhistle}</span>}
                      </div>
                      
                      {formData.isOnFirstWhistle && (
                        <div className="form-group">
                          <label htmlFor="teamName">Team Name</label>
                          <input
                            type="text"
                            id="teamName"
                            name="teamName"
                            value={formData.teamName}
                            onChange={handleChange}
                            placeholder="Your team name"
                            className={formErrors.teamName ? 'error' : ''}
                          />
                          {formErrors.teamName && <span className="error-message">{formErrors.teamName}</span>}
                        </div>
                      )}
                      
                      {formData.isOnFirstWhistle === 'false' && (
                        <>
                          <div className="form-group">
                            <label htmlFor="managerName">Manager Name</label>
                            <input
                              type="text"
                              id="managerName"
                              name="managerName"
                              value={formData.managerName}
                              onChange={handleChange}
                              placeholder="Team manager's name"
                              className={formErrors.managerName ? 'error' : ''}
                            />
                            {formErrors.managerName && <span className="error-message">{formErrors.managerName}</span>}
                          </div>
                          
                          <div className="form-group">
                            <label htmlFor="managerMobile">Manager Mobile</label>
                            <input
                              type="tel"
                              id="managerMobile"
                              name="managerMobile"
                              value={formData.managerMobile}
                              onChange={handleChange}
                              placeholder="Team manager's mobile number"
                              className={formErrors.managerMobile ? 'error' : ''}
                            />
                            {formErrors.managerMobile && <span className="error-message">{formErrors.managerMobile}</span>}
                          </div>
                          
                          <div className="info-message">
                            <p>As your team is currently not on FirstWhistle, we need to contact your club.</p>
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="teamName">Team Name</label>
                      <input
                        type="text"
                        id="teamName"
                        name="teamName"
                        value={formData.teamName}
                        onChange={handleChange}
                        placeholder="Your team name"
                        className={formErrors.teamName ? 'error' : ''}
                      />
                      {formErrors.teamName && <span className="error-message">{formErrors.teamName}</span>}
                    </div>
                  )}
                  
                  <div className="form-group recaptcha-info">
                    <p>This site is protected by reCAPTCHA v3.</p>
                  </div>
                  
                  <button 
                    type="submit" 
                    className="submit-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Join Now'}
                  </button>
                </form>
              </div>
            )}
            
            <div className="faq-section">
              <h2>Frequently Asked Questions</h2>
              <div className="faq-grid">
                <div className="faq-item">
                  <h3>What is FirstWhistle?</h3>
                  <p>FirstWhistle is an AI-powered football team management app designed specifically for grassroots teams, helping with player availability, payments, and team selection.</p>
                </div>
                <div className="faq-item">
                  <h3>Is FirstWhistle free to use?</h3>
                  <p>Yes! FirstWhistle is completely free for Coaches and Managers. Players and Parents can access basic features for free, with premium features available for just £2.50.</p>
                </div>
                <div className="faq-item">
                  <h3>How does the AI team selection work?</h3>
                  <p>Our AI analyses player statistics, availability, and performance data to suggest optimal team formations and lineups, helping managers make informed decisions.</p>
                </div>
                <div className="faq-item">
                  <h3>Can I track player payments through the app?</h3>
                  <p>Yes, FirstWhistle includes a built-in payment tracking system to manage match fees, subscriptions, and other team-related expenses.</p>
                </div>
                <div className="faq-item">
                  <h3>What platforms will FirstWhistle be available on?</h3>
                  <p>FirstWhistle will be available on both iOS and Android devices via a web interface. You can bookmark it so that it appears as a native iOS / Google app. It will also be available on a desktop via firstwhistle.app.</p>
                </div>
                <div className="faq-item">
                  <h3>How secure is my team's data?</h3>
                  <p>We take data security seriously. All team and player information is encrypted and stored securely following industry best practices.</p>
                </div>
                <div className="faq-item">
                  <h3>Can multiple team managers use the same account?</h3>
                  <p>Yes, FirstWhistle supports multiple admin roles, allowing different team officials to manage various aspects of the team.</p>
                </div>
                <div className="faq-item">
                  <h3>When will FirstWhistle be available?</h3>
                  <p>FirstWhistle is currently in development. Join now to be among the first to access the platform when we launch.</p>
                </div>
              </div>
            </div>
            
            <div className="contact-section">
              <h2>Get in Touch</h2>
              <p>Have any questions or need assistance? We're here to help!</p>
              
              <div className="contact-container">
                <div className="contact-info">
                  <div className="contact-item">
                    <FaPhoneAlt />
                    <h3>Call Us</h3>
                    <p>Give us a call at <strong>+44 (0) 7875 930 733</strong></p>
                  </div>
                  
                  <div className="contact-item">
                    <h3>Follow Us</h3>
                    <p>Stay connected with us on social media</p>
                    <div className="social-links">
                      <a href="https://www.facebook.com/FirstWhistleApp" className="social-link" target="_blank" rel="noopener noreferrer"><FaFacebookF /></a>
                      <a href="https://www.instagram.com/firstwhistle.app" className="social-link" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
                    </div>
                  </div>
                </div>
                
                <div className="contact-form-container">
                  {contactSubmitStatus === 'success' ? (
                    <div className="contact-success">
                      <div className="success-icon">
                        <FaCheckCircle />
                      </div>
                      <h3>Message Sent!</h3>
                      <p>Thank you for reaching out. We'll get back to you as soon as possible.</p>
                      <button 
                        className="secondary-button"
                        onClick={() => {
                          setContactData({ name: '', message: '' });
                          setContactErrors({});
                          setContactSubmitStatus(null);
                        }}
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form className="contact-form" onSubmit={handleContactSubmit}>
                      {contactSubmitStatus === 'error' && contactErrors.submit && (
                        <div className="error-message global-error">
                          {contactErrors.submit}
                        </div>
                      )}
                      
                      <InputField
                        label="Your Name"
                        name="name"
                        value={contactData.name}
                        onChange={handleContactChange}
                        placeholder="Enter your name"
                        error={contactErrors.name}
                        required={true}
                      />
                      
                      <div className="form-group">
                        <label>Your Message <span className="required">*</span></label>
                        <textarea
                          name="message"
                          value={contactData.message}
                          onChange={handleContactChange}
                          placeholder="What would you like to know?"
                          className={contactErrors.message ? 'error' : ''}
                        ></textarea>
                        {contactErrors.message && <span className="error-message">{contactErrors.message}</span>}
                      </div>
                      
                      <button 
                        type="submit" 
                        className="contact-submit-button"
                        disabled={contactSubmitStatus === 'submitting'}
                      >
                        {contactSubmitStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
            
            <p className="small-text">FirstWhistle is an AI-powered football team management application tailored for grassroots teams. Our features include tracking player availability, managing payments, viewing leaderboards, accessing player statistics, recruiting new players, and receiving timely notifications for team selections. Simplify your football management experience with FirstWhistle.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup; 